import { subscribeAuth, getFirebaseAuth, peekSignInIsNewUser, peekSignInWasLink, type AuthUser } from "./authClient";
import posthog, { isPostHogConfigured } from "./posthog";
import { identify, addBreadcrumb, setUserProperty, track } from "./telemetry";
import { STRIPE_ROLE_CLAIM, PREMIUM_ROLE } from "./premium";
import { getFirstTouch } from "./attribution";
import {
  classifyAuthTransition,
  consumePendingSignIn,
  hasPendingSignIn,
  isNewAccount,
} from "./signInFunnel";
import { persistAnalyticsIdentity } from "./analyticsIdentity";
import { startReverseTrial } from "./premium";
import { getFlag } from "./flags";

/**
 * Bridge Firebase Auth state into the telemetry facade so Cloud
 * Error Reporting POSTs carry the right `user` field and Firebase
 * Analytics events carry the right User-ID + user properties.
 *
 * Why a separate module: keeping this out of `telemetry.ts` means
 * the facade has its own minimal Firebase touch (just the app
 * handle for Analytics), and unit tests for any module that imports
 * `telemetry` don't need an auth mock.
 *
 * Behavior:
 *   - Every signed-in snapshot: identify the PostHog browser session with
 *     the stable Firebase `uid`, cache it in the facade (used as the Error
 *     Reporting `context.user`), set it as the GA4 User-ID, and stamp
 *     `is_premium` (from the `stripeRole` claim).
 *   - A real sign-in (see {@link classifyAuthTransition}) additionally
 *     fires `sign_up` for a brand-new account or `login` otherwise. A
 *     restored session on page load fires neither.
 *   - On sign-out: drop both — the uid stops appearing in error
 *     reports, and GA4 stops associating subsequent events with
 *     the prior user.
 *
 * Idempotent — call from `main.tsx` exactly once.
 */

let initialized = false;
let unsub: (() => void) | null = null;
let hadUser = false;
let sawFirstSnapshot = false;
/** The previous snapshot was an anonymous session (and its uid). */
let lastWasAnonymous = false;
let lastUid: string | null = null;
/** Distinct id set in the browser SDK during this page lifetime. */
let postHogDistinctId: string | null = null;

function syncPostHogIdentity(user: AuthUser): void {
  if (!isPostHogConfigured) return;

  const properties: Record<string, string | boolean> = {
    is_anonymous: user.isAnonymous,
  };
  // PII belongs on the PostHog person, never on captured event properties.
  if (!user.isAnonymous && user.email) properties.email = user.email;
  if (!user.isAnonymous && user.displayName) properties.name = user.displayName;

  if (postHogDistinctId === user.uid) {
    // An anonymous Firebase session can become an account without changing
    // uid. Update its person properties without repeating identify.
    posthog.setPersonProperties(properties);
    return;
  }
  if (postHogDistinctId) posthog.reset();
  posthog.identify(user.uid, properties);
  postHogDistinctId = user.uid;
}

function resetPostHogIdentity(): void {
  if (!isPostHogConfigured || !postHogDistinctId) return;
  posthog.reset();
  postHogDistinctId = null;
}

export function initTelemetryAuthBridge(): void {
  if (initialized) return;
  initialized = true;

  unsub = subscribeAuth((user) => {
    const firstSnapshot = !sawFirstSnapshot;
    sawFirstSnapshot = true;
    if (!user) {
      resetPostHogIdentity();
      identify(null);
      if (hadUser && !lastWasAnonymous) track("signed_out");
      hadUser = false;
      lastWasAnonymous = false;
      lastUid = null;
      addBreadcrumb("auth:signed-out");
      return;
    }
    // An anonymous session (the first run's "try before you sign up") is a
    // uid without an account: identify it so its events join up with the
    // account it may become, but never count it as a sign-in.
    if (user.isAnonymous) {
      const fresh = !hadUser || lastUid !== user.uid;
      syncPostHogIdentity(user);
      identify(user, { is_premium: false, is_anonymous: true });
      if (fresh) track("anonymous_session_started");
      addBreadcrumb("auth:anonymous", { fresh });
      hadUser = true;
      lastWasAnonymous = true;
      lastUid = user.uid;
      return;
    }
    syncPostHogIdentity(user);
    const current = getFirebaseAuth()?.currentUser;
    // Same uid, no longer anonymous = the session was linked to an
    // account (Google / Apple popup-link, or the email code on the server).
    const linked = (lastWasAnonymous && lastUid === user.uid) || peekSignInWasLink(user.uid);
    const transition = classifyAuthTransition({
      firstSnapshot,
      wasSignedIn: hadUser && !lastWasAnonymous,
      pendingSignIn: hasPendingSignIn(),
      lastSignInTime: current?.metadata?.lastSignInTime,
      now: Date.now(),
    });
    hadUser = true;
    lastWasAnonymous = false;
    lastUid = user.uid;
    // Best-effort read of the premium claim so GA4 can segment free
    // vs premium users without round-tripping our `premium.ts`
    // store (which lazily subscribes; we'd otherwise need to wait
    // for the publishedState to settle). Failure here is fine —
    // the identify still goes through with just the uid.
    void resolvePremiumProp(user.uid).then((extras) => {
      identify(user, { ...extras, is_anonymous: false });
      addBreadcrumb("auth:signed-in", { is_premium: extras.is_premium, transition, linked });
      if (linked) emitLinked(user.uid);
      else if (transition === "sign_in") emitSignIn(user.uid);
      void persistAnalyticsIdentity(user.uid);
    });
  });
}

/**
 * An anonymous session became an account with the same uid. That is the
 * registration conversion (`sign_up` → Meta CompleteRegistration) plus an
 * `account_linked` beat so the funnel can tell "tried first" from "signed
 * up cold".
 */
function emitLinked(uid: string): void {
  const pending = consumePendingSignIn();
  const surface = pending?.surface ?? "unknown";
  const method = signInMethod();
  const firstTouch = stampNewAccount();
  track("account_linked", { method, surface });
  track("sign_up", { method, surface, ...firstTouch });
  addBreadcrumb("auth:linked", { uid, method, surface });
  maybeStartTrial();
}

/**
 * Stamp a new account with its sign-up date and first-touch UTMs (down to
 * the ad creative, `utm_content`) as user properties, and return the UTMs
 * for `sign_up`.
 */
function stampNewAccount() {
  const firstTouch = getFirstTouch();
  const props = {
    first_touch_source: firstTouch?.utm_source ?? null,
    first_touch_medium: firstTouch?.utm_medium ?? null,
    first_touch_campaign: firstTouch?.utm_campaign ?? null,
    first_touch_content: firstTouch?.utm_content ?? null,
  };
  setUserProperty("signup_date", new Date().toISOString().slice(0, 10));
  for (const [name, value] of Object.entries(props)) setUserProperty(name, value);
  return props;
}

/** New accounts get the reverse trial (Premium for a week, no card). */
function maybeStartTrial(): void {
  if (!getFlag("reverse_trial")) return;
  void startReverseTrial().then((res) => {
    if (res?.started) track("reverse_trial_started", { days: 7 });
  });
}

function emitSignIn(uid: string): void {
  const pending = consumePendingSignIn();
  const surface = pending?.surface ?? "unknown";
  const method = signInMethod();
  const flagged = peekSignInIsNewUser(uid);
  const isNew = flagged ?? isNewAccount(getFirebaseAuth()?.currentUser?.metadata);
  if (!isNew) {
    track("login", { method, surface });
    return;
  }
  track("sign_up", { method, surface, ...stampNewAccount() });
  maybeStartTrial();
}

function signInMethod(): string {
  const current = getFirebaseAuth()?.currentUser;
  const provider = current?.providerData[0]?.providerId;
  if (provider === "google.com") return "google";
  if (provider === "apple.com") return "apple";
  if (provider === "password") return "password";
  // The email one-time code signs in with a custom token: no provider
  // entry, but the server set a verified email on the user.
  if (!provider && current?.email) return "email";
  return provider ?? "unknown";
}

async function resolvePremiumProp(uid: string): Promise<{ is_premium: boolean }> {
  const auth = getFirebaseAuth();
  if (!auth || !auth.currentUser || auth.currentUser.uid !== uid) {
    return { is_premium: false };
  }
  try {
    const result = await auth.currentUser.getIdTokenResult(false);
    const role = (result.claims as Record<string, unknown>)[STRIPE_ROLE_CLAIM];
    return { is_premium: role === PREMIUM_ROLE };
  } catch {
    return { is_premium: false };
  }
}

/** Test-only: tear down the singleton so tests start clean. */
export function _resetTelemetryAuthBridgeForTests(): void {
  if (unsub) unsub();
  unsub = null;
  initialized = false;
  hadUser = false;
  sawFirstSnapshot = false;
  lastWasAnonymous = false;
  lastUid = null;
  postHogDistinctId = null;
}
